import type { ReactElement } from 'react'

import generatePicker from 'antd/es/date-picker/generatePicker'
import type {
    RangePickerProps as AntdRangePickerProps,
    PickerClassNames,
    PickerProps,
    PickerPropsWithMultiple,
} from 'antd/es/date-picker/generatePicker/interface'
import classNames from 'classnames'
import type { GenerateConfig } from 'rc-picker/es/generate'
import dayjsGenerateConfig from 'rc-picker/es/generate/dayjs'

import { ChevronIcon } from 'components/Icons'
import { date, type Dayjs } from 'configuration/dayjs'

import * as styles from './styles.module.scss'

// antd builds every date it creates (today, parsed input text) from a local-time
// dayjs. Re-anchoring those in UTC keeps picked values on the same instants as the
// rest of the app. Values passed in keep their own zone, so a .tz() value shows
// that zone's calendar day.
export const utcGenerateConfig: GenerateConfig<Dayjs> = {
    ...dayjsGenerateConfig,
    getNow: () => date(),
    getFixedDate: (fixed) => dayjsGenerateConfig.getFixedDate(fixed).utc(true),
    locale: {
        ...dayjsGenerateConfig.locale,
        parse: (locale, text, formats) =>
            dayjsGenerateConfig.locale
                .parse(locale, text, formats)
                ?.utc(true) ?? null,
    },
}

const AntdUtcDatePicker = generatePicker<Dayjs>(utcGenerateConfig)

type DatePickerProps<ValueType> = PickerPropsWithMultiple<
    Dayjs,
    PickerProps<Dayjs>,
    ValueType
>
type RangePickerProps = AntdRangePickerProps<Dayjs>

function withCalendarPopup(
    pickerClassNames?: PickerClassNames
): PickerClassNames {
    return {
        ...pickerClassNames,
        popup: {
            ...pickerClassNames?.popup,
            root: classNames(styles.calendar, pickerClassNames?.popup?.root),
        },
    }
}

const calendarNavIcons = {
    prevIcon: <ChevronIcon rotate={180} />,
    nextIcon: <ChevronIcon />,
}

function DatePicker<ValueType = Dayjs>(
    props: DatePickerProps<ValueType>
): ReactElement {
    const { classNames: pickerClassNames } = props
    return (
        <AntdUtcDatePicker<ValueType>
            {...calendarNavIcons}
            {...props}
            classNames={withCalendarPopup(pickerClassNames)}
        />
    )
}

function RangePicker(props: RangePickerProps): ReactElement {
    const { classNames: pickerClassNames } = props
    return (
        <AntdUtcDatePicker.RangePicker
            {...calendarNavIcons}
            {...props}
            classNames={withCalendarPopup(pickerClassNames)}
        />
    )
}

export const UtcDatePicker = Object.assign(DatePicker, { RangePicker })
